/**
 * The VALUES shared by the two ADDIS status surfaces (A7Y-3808) — the compact
 * board on `/admin/automation` and the full status block on each system's own
 * page. Their shared COMPONENTS live beside this file in
 * `addis-status-parts.tsx`.
 *
 * Split for the same reason `quota-account-meta.ts` is: a module that exports
 * both components and plain values loses React fast refresh, and these three
 * are read by non-component code (the row's record cell, the route helper) as
 * well as by the badges.
 */
import type { AddisLight } from '@a7y/types';

/**
 * The glyph for each light, and the word for the light that is NOT rendered.
 *
 * `label` survives because the badge still needs it for its accessible name and
 * its tooltip — the glyph is the whole visible content, so dropping the word
 * from the map too would leave a coloured dot with no text equivalent for a
 * screen reader, which is the one reader that cannot see the colour. There is
 * no `className`: it existed to colour the word, and the emoji carries its own
 * colour.
 */
export const LIGHT_PRESENTATION: Record<AddisLight, { glyph: string; label: string }> = {
  green: { glyph: '🟢', label: 'Green' },
  amber: { glyph: '🟡', label: 'Amber' },
  red: { glyph: '🔴', label: 'Red' },
};

/** GitHub's page for one workflow file's runs — where a group row's identity
 *  points. The ticks link to single runs; this is the remaining hop. */
export function workflowRunsUrl(workflow: string): string {
  return `https://github.com/ApptonomyInc/a7y/actions/workflows/${workflow}`;
}

/**
 * Where a workflow's runs are listed INSIDE the console, for the rows that have
 * such a page.
 *
 * Deliberately short and conservative. A page earns an entry only if it really
 * lists that workflow's runs: `cdid-worker.yml` is here because CDID's Activity
 * tab nests workers under their orchestrator, while `cdid-lander.yml` and
 * `cdid-hot-lane.yml` are NOT — the lander writes no flight-recorder rows at
 * all, and the hot lane's `cdid-hot-lane` session kind is outside the Activity
 * query's filter. Pointing either at CDID's Activity would send the reader to a
 * page that never mentions the run they clicked from.
 *
 * Everything absent falls back to the workflow's own runs page on GitHub, which
 * is always correct and always exists.
 */
const CONSOLE_RUN_LISTS: Record<string, string> = {
  'cdid.yml': '/admin/automation/cdid',
  'cdid-worker.yml': '/admin/automation/cdid',
  'adgs.yml': '/admin/automation/adgs',
  'apd.yml': '/admin/automation/apd',
  'ahc.yml': '/admin/platform-health/ahc',
  'state-invariants.yml': '/admin/platform-health/state-invariants',
};

/** An in-app route, or null when only GitHub lists this workflow's runs. */
export function consoleRunListPath(workflow: string): string | null {
  return CONSOLE_RUN_LISTS[workflow] ?? null;
}

/**
 * Where a row's "detail" goes: the console's session detail when the latest run
 * produced a session, otherwise that run on GitHub.
 *
 * Returns `null` only when there is no latest run at all — a reusable workflow
 * such as `aquix-runner.yml`, whose runs belong to its caller, or one that has
 * not run inside the window. A row with nothing to point at renders no link
 * rather than a dead one.
 */
export function runDetailTarget(health: {
  latest?: { url: string } | null;
  latestSession?: { kind: string; stamp: string } | null;
}): { to: string } | { href: string } | null {
  if (health.latestSession) {
    return { to: `/admin/automation/${health.latestSession.kind}/${health.latestSession.stamp}` };
  }
  return health.latest ? { href: health.latest.url } : null;
}
